import { type FC, useState } from 'react'
import classNames from 'classnames'
import { type TableColumn } from 'react-data-table-component'
import { Button } from '../../../components/Button/Button'
import { ZocksDataTable } from '../../../components/DataTable/ZocksDataTable'
import { IntegrationIcon } from '../../../components/IntegrationIcon'
import { INTEGRATION_DISPLAY_NAME_MAP, type IntegrationServiceId } from '../../../common/integrations'
import { MAX_PAGE_SIZE } from '../../../common/constants'
import { formatFriendlyDateTime } from '../../../common/dateUtils'
import { useDeleteAccountOpeningMutation, useListAccountOpeningsQuery } from '../../../store/accountOpening/accountOpening'
import { type AccountOpeningDTO, type AccountType } from '../../../models/AccountOpening'
import { ConfirmDeleteSubmissionModal } from './ConfirmDeleteSubmissionModal'
import { statusLabel, statusLabelsOf, storedStatusCode } from '../domain/connectorResult'
import { ErrorText, SpinnerLine } from '../steps/flowPrimitives'
import { axisLabel, findAccountType } from '../domain/accountTypeCatalog'
import { NO_VALUE, openingsCopy, WIZARD_ERRORS } from '../descriptor/stepCopy'
import { type ResolvedCatalog } from '../hooks/useAccountTypeCatalog'

interface OpeningsEntryPanelProps {
  accountId: string
  connectors: IntegrationServiceId[]
  contactId: string | null
  catalog: ResolvedCatalog
  onOpenNew: () => void
  onResume: (openingId: string) => void
  onView: (openingId: string) => void
  busyOpeningId: string | null
}

const isDraft = (opening: AccountOpeningDTO): boolean => opening.status === 'draft'

const holderNames = (opening: AccountOpeningDTO): string =>
  (opening.customers ?? [])
    .map((customer) => customer.displayName?.trim() ?? '')
    .filter(Boolean)
    .join(', ')

const openingTimestamp = (opening: AccountOpeningDTO): string => (isDraft(opening) ? opening.createdAt : opening.submittedAt) ?? ''

const openingDate = (opening: AccountOpeningDTO): string => {
  const raw = openingTimestamp(opening)
  return raw ? formatFriendlyDateTime(new Date(raw)) : NO_VALUE
}

const axisColumn = (accountTypes: AccountType[], opening: AccountOpeningDTO, field: string, isFirstAxis: boolean): string => {
  const values = opening.accounts.map((account) => {
    const type = findAccountType(accountTypes, account.typeId)
    return type ? axisLabel(type, field) : isFirstAxis ? account.typeId : ''
  })
  return [...new Set(values.filter(Boolean))].join(', ') || NO_VALUE
}

export const OpeningsEntryPanel: FC<OpeningsEntryPanelProps> = ({ accountId, connectors, contactId, catalog, onOpenNew, onResume, onView, busyOpeningId }) => {
  const listArgs = { accountId, pageRequest: { page: 0, size: MAX_PAGE_SIZE }, ...(contactId ? { contactId } : {}) }
  const showsCustodian = connectors.length > 1
  const { accountTypes, selectors, descriptor, isLoadingAccountTypes } = catalog
  const copy = openingsCopy(descriptor)
  const { data: drafts, isFetching: isFetchingDrafts } = useListAccountOpeningsQuery({ ...listArgs, status: 'draft' }, { refetchOnMountOrArgChange: true })
  const { data: submitted, isFetching: isFetchingSubmitted } = useListAccountOpeningsQuery(
    { ...listArgs, status: 'submitted' },
    { refetchOnMountOrArgChange: true },
  )
  const [deleteAccountOpening, { isLoading: isDeleting }] = useDeleteAccountOpeningMutation()
  const [pendingDeletion, setPendingDeletion] = useState<AccountOpeningDTO | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const openings = [...(drafts?.data ?? []), ...(submitted?.data ?? [])]
  const isFetching = isFetchingDrafts || isFetchingSubmitted || isLoadingAccountTypes

  const handleDelete = async (openingId: string) => {
    setDeleteError(null)
    const result = await deleteAccountOpening({ accountId, openingId })
    if ('error' in result) {
      setDeleteError(WIZARD_ERRORS.delete)
    }
  }

  const handleConfirmDeletion = async () => {
    const opening = pendingDeletion
    setPendingDeletion(null)
    if (opening) {
      await handleDelete(opening.id)
    }
  }

  const statusLabels = statusLabelsOf(descriptor)

  const columns: TableColumn<AccountOpeningDTO>[] = [
    ...(showsCustodian
      ? [
          {
            name: 'Custodian',
            cell: (opening: AccountOpeningDTO) => (
              <span className='d-flex align-items-center zocks-gap-0.5'>
                <IntegrationIcon serviceId={opening.connector as IntegrationServiceId} width={20} height={20} />
                {INTEGRATION_DISPLAY_NAME_MAP[opening.connector as IntegrationServiceId] ?? opening.connector}
              </span>
            ),
          },
        ]
      : []),
    ...selectors.map((selector, axisIndex) => ({
      name: selector.label,
      selector: (opening: AccountOpeningDTO) => axisColumn(accountTypes, opening, selector.field, axisIndex === 0),
      sortable: true,
    })),
    { name: 'Holder(s)', selector: (opening: AccountOpeningDTO) => holderNames(opening) || NO_VALUE, sortable: true, grow: 2 },
    { name: 'Date', selector: openingTimestamp, format: openingDate, sortable: true },
    {
      name: 'Status',
      cell: (opening: AccountOpeningDTO) => (
        <span
          className={classNames(
            'paragraph-small medium rounded-1 px-2 py-1 d-inline-block',
            isDraft(opening) ? 'zocks-bg-color-warning' : 'zocks-bg-color-success-background',
          )}
        >
          {isDraft(opening) ? copy.draftStatus : statusLabel(statusLabels, storedStatusCode(opening.metadata)) || copy.submittedStatus}
        </span>
      ),
    },
    {
      right: true,
      allowOverflow: true,
      width: '7rem',
      cell: (opening: AccountOpeningDTO) => (
        <div className='d-flex justify-content-end zocks-gap-0.5'>
          <Button
            variant='secondary'
            aria-label={isDraft(opening) ? 'Continue application' : 'View submission'}
            loading={busyOpeningId === opening.id}
            disabled={busyOpeningId === opening.id}
            onClick={() => (isDraft(opening) ? onResume(opening.id) : onView(opening.id))}
          >
            <i className={classNames('bi', isDraft(opening) ? 'bi-pencil-square' : 'bi-eye')} />
          </Button>
          <Button
            variant='secondary'
            aria-label={isDraft(opening) ? 'Delete application' : 'Delete submission'}
            disabled={isDeleting}
            onClick={() => (isDraft(opening) ? handleDelete(opening.id) : setPendingDeletion(opening))}
          >
            <i className='bi bi-trash' />
          </Button>
        </div>
      ),
    },
  ]

  return (
    <div className='d-flex flex-column zocks-gap-1.5'>
      <div className='d-flex align-items-center justify-content-between zocks-gap-1'>
        <span className='paragraph-medium'>Submissions sent from Zocks</span>
        <Button variant='primary' className='d-flex align-items-center zocks-gap-0.5 flex-shrink-0' onClick={onOpenNew}>
          <i className='bi bi-file-earmark-plus' />
          New account
        </Button>
      </div>

      {deleteError ? <ErrorText>{deleteError}</ErrorText> : null}

      <ZocksDataTable
        columns={columns}
        data={openings}
        keyField='id'
        pagination
        progressPending={isFetching}
        progressComponent={<SpinnerLine className='py-3'>Loading applications…</SpinnerLine>}
        noDataComponent={<p className='paragraph-small zocks-text-gray-600 mb-0 py-3'>{copy.empty}</p>}
      />

      {pendingDeletion ? (
        <ConfirmDeleteSubmissionModal opening={pendingDeletion} copy={copy} onCancel={() => setPendingDeletion(null)} onConfirm={handleConfirmDeletion} />
      ) : null}
    </div>
  )
}
